import { useMemo, useState, useCallback, useRef } from 'react';
import styled from '@emotion/styled';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { theme } from '../styles/theme';
import { getExpenditureTypeColor, getSubTypeColor, SONSTIGES_COLOR, SONSTIGES_ID } from '../utils/colors';
import { formatTooltipValue, formatGdpPercentage, formatGdpPercentageTick, createAxisFormatter, getAxisUnit } from '../utils/formatters';
import { buildFilterInfoText, type ActiveFilterValues, type FilterOptionsLookup } from '../utils/filterInfo';
import { useFlushLeftYAxisWidth } from '../hooks/useFlushLeftYAxisWidth';
import { Section, CardSubtitle, FlexRow, Button, IconButton, Select, Spinner, LoadingOverlay } from '../styles/components';
import { ExportDropdown } from './ExportDropdown';
import { EmbedButton } from './EmbedButton';
import { exportChartAsPng, exportChartAsSvg, type ChartExportOptions, type LegendItem } from '../utils/chartExport';
import { categoryLabel, useT } from '../i18n';
import type { ExpenditureTypeData, TitleData, SelectedTitle, DisplayUnit, GdpData, BudgetTotalData } from '../types';

const HAUSHALTSTECHNIK_TYPE = "Haushaltstechnik & Sonderposten";

const ChartContainer = styled.div`
  & *:focus {
    outline: none;
  }
`;

// Unit label above the y-axis — publishing chart engine: ABC Social 13px
// black, written ONCE as a caption anchored at the axis line and extending
// right into the plot (createChart.js drawYAxis: x=0, text-anchor start).
const AxisUnitLabel = styled.div`
  font-family: var(--sans-stack);
  font-size: 13px;
  color: #000000;
  text-align: left;
  white-space: nowrap;
  margin-bottom: 2px;
`;

// Icon-only back affordance built on the shared `.button` family (ghost
// variant) rather than the bespoke IconButton, so it inherits the shared
// button sizing/hover/focus treatment. Trimmed to a square padding and given
// a 20px glyph to read as a compact icon button.
const BackButton = styled(Button)`
  &.button {
    padding: ${theme.spacing.xs};
    color: ${theme.colors.primary};
  }
  .icon {
    font-size: 20px;
    display: block;
  }
`;

const YearSelect = styled(Select)`
  min-width: 100px;
`;

const ActiveFilterChip = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.xl};
  background: rgba(24, 28, 68, 0.1);
`;

const ChipText = styled.span`
  font-family: ${theme.fontFamily};
  font-size: ${theme.fontSize.sm};
  font-weight: ${theme.fontWeight.medium};
  color: ${theme.colors.primary};
`;

const ClearChipButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  font-size: 14px;
  border: none;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  color: ${theme.colors.primary};
  transition: all 0.2s;

  &:hover {
    background: rgba(24, 28, 68, 0.2);
  }
`;

// Legend pills — publishing chart engine / Figma: white chips, 19px radius,
// 5px/9px padding, 26px height, 10px round dot, 13px black ABC Social label.
// Bottom legends are LEFT-aligned against the plot/title edge (createLegend.js
// Figma rule); only top legends are centered.
const LegendContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 8px;
  margin-top: ${theme.spacing.md};
`;

const LegendItem = styled.div<{ dimmed?: boolean; isClickable?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 9px;
  border-radius: 19px;
  background: #FFFFFF;
  cursor: ${props => props.isClickable === false ? 'default' : 'pointer'};
  opacity: ${props => props.dimmed ? 0.4 : 1};
  transition: opacity 0.2s;

  &:hover {
    opacity: ${props => props.isClickable === false ? props.dimmed ? 0.4 : 1 : 1};
  }
`;

const LegendDot = styled.span<{ color: string; dashed?: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  background-color: ${props => props.dashed ? 'transparent' : props.color};
  ${props => props.dashed && `
    border: 1px dashed #000000;
  `}
`;

const LegendLabel = styled.span`
  font-family: var(--sans-stack);
  font-size: 13px;
  line-height: 16px; /* 5+16+5 = 26px chip height per Figma layout_LZS74O */
  color: #000000;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 300px;
  font-family: ${theme.fontFamily};
  font-size: ${theme.fontSize.sm};
  color: ${theme.colors.text.secondary};
`;

const Caption = styled.p`
  font-family: ${theme.fontFamily};
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.text.muted};
  text-align: left;
  margin-top: ${theme.spacing.sm};
`;

// Tooltip chrome — publishing chart engine: white background, 1px solid
// black border, 8px padding, square corners, no shadow (.tooltip in
// createChart.js).
const TooltipContainer = styled.div`
  background: #FFFFFF;
  border: 1px solid #000000;
  padding: 8px;
  max-width: 300px;
`;

const TooltipHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.xs};
`;

const TooltipTitle = styled.span`
  font-family: ${theme.fontFamily};
  font-size: ${theme.fontSize.sm};
  font-weight: ${theme.fontWeight.semibold};
  color: ${theme.colors.text.primary};
`;

const TooltipSubtitle = styled.span`
  font-family: ${theme.fontFamily};
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.text.secondary};
  margin-bottom: ${theme.spacing.xs};
  display: block;
`;

const TooltipValue = styled.span`
  font-family: ${theme.fontFamily};
  font-size: ${theme.fontSize.lg};
  font-weight: ${theme.fontWeight.bold};
  color: ${theme.colors.text.primary};
`;

// SVG Icons
const BackIcon = () => (
  <span className="icon" data-icon="arrow_back" />
);

const ClearIcon = () => (
  <span className="icon" data-icon="close" />
);

const ChevronIcon = ({ isExpanded }: { isExpanded: boolean }) => (
  <span
    className="icon"
    data-icon="keyboard_arrow_down"
    style={{ fontSize: 20, transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
  />
);

const ExpandButton = styled(IconButton)`
  color: ${theme.colors.text.secondary};
  &:hover {
    background: rgba(24, 28, 68, 0.1);
    color: ${theme.colors.primary};
  }
`;

// Mini preview styled components for collapsed state
const CollapsedPreview = styled.div`
  padding: ${theme.spacing.md};
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.border};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${theme.colors.background};
    border-color: ${theme.colors.primary};
  }
`;

const PreviewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.sm};
`;

const PreviewLeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  flex: 1;
  min-width: 0;
`;

const PreviewYear = styled.span`
  font-family: ${theme.fontFamily};
  font-size: ${theme.fontSize.lg};
  font-weight: ${theme.fontWeight.bold};
  color: ${theme.colors.text.primary};
  white-space: nowrap;
`;

// Container for mini vertical bars (inline)
const MiniChartContainer = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 3px;
  height: 24px;
  flex: 1;
  min-width: 0;
`;

// Individual vertical bar
const MiniBar = styled.div<{ height: string; color: string }>`
  flex: 1;
  height: ${props => props.height};
  min-width: 4px;
  max-width: 20px;
  background-color: ${props => props.color};
  border-radius: 2px 2px 0 0;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;

const ExpandLabel = styled.span`
  font-family: ${theme.fontFamily};
  font-size: ${theme.fontSize.sm};
  color: ${theme.colors.text.secondary};
  white-space: nowrap;
`;

// Breadcrumb header — same shape as BudgetTable's: last item = serif-l
// chart title (matches dz-shared `@mixin serif-l`); earlier crumbs = small
// sans muted; sans separator. Keeps chart title visually aligned with the
// table title elsewhere in the dashboard.
const BreadcrumbContainer = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: ${theme.spacing.xs};
`;

const BreadcrumbItem = styled.span<{ isLast: boolean; isClickable: boolean }>`
  ${props => props.isLast ? `
    font-family: var(--serif-stack);
    font-size: 1.875rem;
    line-height: 1.15;
    letter-spacing: -0.02em;
    font-weight: normal;
    color: var(--color-text-default);
  ` : `
    font-family: var(--sans-stack);
    font-size: 1rem;
    line-height: 1.2;
    color: var(--color-text-muted);
  `}
  cursor: ${props => props.isClickable ? 'pointer' : 'default'};

  ${props => props.isClickable && `
    &:hover {
      color: var(--primary-color);
      text-decoration: underline;
    }
  `}
`;

const BreadcrumbSeparator = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 1rem;
  color: var(--color-text-muted);
  margin: 0 ${theme.spacing.xs};
`;

const FilterInfoLine = styled.div`
  font-family: var(--sans-stack);
  font-size: 0.75rem;
  color: var(--color-text-muted);
  margin-top: ${theme.spacing.xs};
`;

interface BarChartDataItem {
  type: string;
  displayName?: string;  // For title level: human-readable name (type is id)
  value: number;
  isIst: boolean;
  color: string;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    payload?: BarChartDataItem;
  }>;
  formatter?: (value: number) => string;
}

function CustomTooltip({ active, payload, formatter = formatTooltipValue }: CustomTooltipProps) {
  const m = useT();
  if (!active || !payload || payload.length === 0) return null;

  const entry = payload[0].payload;
  if (!entry) return null;

  // Use displayName if available (title level), otherwise use type
  const displayLabel = entry.displayName || entry.type;

  return (
    <TooltipContainer>
      <TooltipHeader>
        <LegendDot color={entry.color} />
        <TooltipTitle>{displayLabel}</TooltipTitle>
      </TooltipHeader>
      <TooltipSubtitle>
        {entry.isIst ? m.chart.tooltipIst : m.chart.tooltipSoll}
      </TooltipSubtitle>
      <TooltipValue>{formatter(entry.value)}</TooltipValue>
    </TooltipContainer>
  );
}

interface ExpenditureBarChartProps {
  data: ExpenditureTypeData[];
  subData?: ExpenditureTypeData[] | null;
  titleData?: TitleData[] | null;
  subColorMap?: Map<string, string> | null;
  titleColorMap?: Map<string, string> | null;
  isLoading?: boolean;
  isLoadingSubData?: boolean;
  isLoadingTitleData?: boolean;
  selectedYear: number;
  onYearChange: (year: number) => void;
  drillDownType: string | null;
  drillDownSubType: string | null;
  selectedTitle?: SelectedTitle | null;
  onDrillDown: (type: string) => void;
  onDrillDownSub: (subType: string) => void;
  onDrillUp: () => void;
  onFilterBySubType?: (subType: string) => void;
  onFilterByTitle?: (id: string, zweckbestimmung: string, titelFunktion?: string, prefix?: string) => void;
  onNavigateToRoot?: () => void;
  onNavigateToLevel2?: () => void;
  onDeselectTitle?: () => void;
  activeType?: string | null;
  onClearFilter?: () => void;
  budgetType: 'E' | 'A';
  displayUnit?: DisplayUnit;
  gdpData?: GdpData[] | null;
  budgetTotals?: BudgetTotalData[] | null;
  activeFilters?: ActiveFilterValues;
  filterOptions?: FilterOptionsLookup | null;
  yearsWithIst?: Set<number>;
  /**
   * Render expanded on first paint instead of the collapsed sparkline preview.
   * An embed shows one chart and nothing else, so a "Chart ausklappen" step
   * would leave the iframe showing a teaser.
   */
  startExpanded?: boolean;
  /** Hide the year picker — an embed's year comes from its URL, not the reader. */
  hideYearSelect?: boolean;
  /** Opens the embed-snippet dialog; omitted inside an embed. */
  onEmbed?: () => void;
  /**
   * Hide the PNG/SVG export menu. An embed drops it so the frame needs no icon
   * font at all — see the embed stylesheet, which ships no Material Symbols.
   */
  showExport?: boolean;
  /**
   * false locks the chart down for an embed: bars and legend entries stop
   * inviting clicks that would go nowhere, and the back affordance is dropped.
   * Tooltips and hover highlighting stay — they read the chart, not navigate it.
   */
  interactive?: boolean;
}

export function ExpenditureBarChart({
  data,
  subData,
  titleData,
  subColorMap: propsSubColorMap,
  titleColorMap: propsTitleColorMap,
  isLoading,
  isLoadingSubData,
  isLoadingTitleData,
  selectedYear,
  onYearChange,
  drillDownType,
  drillDownSubType,
  selectedTitle,
  onDrillDown,
  onDrillDownSub,
  onDrillUp,
  onFilterByTitle,
  onNavigateToRoot,
  onNavigateToLevel2,
  onDeselectTitle,
  activeType,
  onClearFilter,
  budgetType,
  displayUnit = 'absolute',
  gdpData,
  budgetTotals,
  activeFilters,
  filterOptions,
  yearsWithIst: propsYearsWithIst,
  startExpanded = false,
  hideYearSelect = false,
  onEmbed,
  showExport = true,
  interactive = true,
}: ExpenditureBarChartProps) {
  const m = useT();
  // Collapsed by default in the dashboard; an embed opens expanded.
  const [isCollapsed, setIsCollapsed] = useState(!startExpanded);
  const chartContainerRef = useRef<HTMLDivElement>(null);

  // Helper function to convert value based on display unit
  const convertValue = useCallback((value: number, year: number): number => {
    if (displayUnit === 'absolute') return value;
    if (displayUnit === 'budget_percent') {
      if (!budgetTotals) return value;
      const yearTotal = budgetTotals.find(t => t.year === year);
      if (!yearTotal) return value;
      const denom = yearTotal.total_ist != null && yearTotal.total_ist > 0
        ? yearTotal.total_ist : yearTotal.total_soll;
      if (denom === 0) return 0;
      return (value / denom) * 100;
    }
    if (!gdpData) return value;
    const gdp = gdpData.find(g => g.year === year);
    if (!gdp) return value;
    // Formula: (budget_value_thousands / (gdp_value_millions * 1000)) * 100
    return (value / (gdp.value * 1000)) * 100;
  }, [displayUnit, gdpData, budgetTotals]);

  // Use centralized yearsWithIst from parent, with fallback
  const globalYearsWithIst = propsYearsWithIst ?? new Set<number>();

  const tooltipValueFormatter = useMemo(() => {
    return displayUnit === 'gdp_percent' || displayUnit === 'budget_percent' ? formatGdpPercentage : formatTooltipValue;
  }, [displayUnit]);

  // Get unique years (descending order)
  const years = useMemo(() => {
    return [...new Set(data.map(d => d.year))].sort((a, b) => b - a);
  }, [data]);

  // Use global Ist year detection (not affected by sidebar filters)
  const yearsWithIst = globalYearsWithIst;

  // Process main chart data for selected year
  const chartData = useMemo((): BarChartDataItem[] => {
    // In budget_percent mode, exclude Haushaltstechnik & Sonderposten
    const filteredData = displayUnit === 'budget_percent'
      ? data.filter(d => d.expenditure_type !== HAUSHALTSTECHNIK_TYPE)
      : data;
    const useIst = yearsWithIst.has(selectedYear);
    return filteredData
      .filter(d => d.year === selectedYear)
      .filter(d => !(useIst && d.total_ist == null))
      .map(d => {
        const rawValue = useIst ? (d.total_ist ?? 0) : d.total_soll;
        return {
          type: d.expenditure_type,
          displayName: categoryLabel(d.expenditure_type),
          value: convertValue(rawValue, selectedYear),
          isIst: useIst && d.total_ist !== null,
          color: getExpenditureTypeColor(d.expenditure_type),
        };
      })
      .sort((a, b) => b.value - a.value);
  }, [data, selectedYear, yearsWithIst, convertValue, displayUnit]);

  // Calculate collapsed state metrics with segments for mini bars
  const collapsedMetrics = useMemo(() => {
    if (!data || data.length === 0) return null;

    const filteredData = displayUnit === 'budget_percent'
      ? data.filter(d => d.expenditure_type !== HAUSHALTSTECHNIK_TYPE)
      : data;
    const useIst = yearsWithIst.has(selectedYear);
    const yearData = filteredData
      .filter(d => d.year === selectedYear)
      .filter(d => !(useIst && d.total_ist == null));
    if (yearData.length === 0) return null;
    const total = yearData.reduce((sum, d) => {
      const rawValue = useIst ? (d.total_ist ?? 0) : d.total_soll;
      return sum + convertValue(rawValue, selectedYear);
    }, 0);

    // Build segments for mini bars (sorted by value descending)
    const segments = yearData
      .map(d => {
        const rawValue = useIst ? (d.total_ist ?? 0) : d.total_soll;
        return {
          name: d.expenditure_type,
          value: convertValue(rawValue, selectedYear),
          color: getExpenditureTypeColor(d.expenditure_type),
        };
      })
      .sort((a, b) => b.value - a.value);

    const maxValue = segments.length > 0 ? segments[0].value : 0;

    return { total, segments, maxValue };
  }, [data, selectedYear, yearsWithIst, convertValue, displayUnit]);

  // Process sub-chart data for drill-down
  const subChartData = useMemo((): BarChartDataItem[] => {
    if (!subData || subData.length === 0) return [];

    // Use global Ist year detection (not affected by sidebar filters)
    const useIst = globalYearsWithIst.has(selectedYear);
    return subData
      .filter(d => d.year === selectedYear)
      .map((d, index) => {
        const rawValue = useIst ? (d.total_ist ?? 0) : d.total_soll;
        return {
          type: d.expenditure_type,
          displayName: categoryLabel(d.expenditure_type),
          value: convertValue(rawValue, selectedYear),
          isIst: useIst && d.total_ist !== null,
          color: propsSubColorMap?.get(d.expenditure_type) ?? getSubTypeColor(index),
        };
      })
      .sort((a, b) => b.value - a.value);
  }, [subData, selectedYear, propsSubColorMap, convertValue, globalYearsWithIst]);

  // Process title data for third drill-down level
  // Key by id (not zweckbestimmung) to consolidate entries with same id but different names
  const titleChartData = useMemo(() => {
    if (!titleData || titleData.length === 0) return { data: [], idMap: new Map<string, string>(), idToTitelFunktionMap: new Map<string, string>(), idToPrefixMap: new Map<string, string>() };

    // Create id -> display name map for selected year (year-specific names)
    const idMap = new Map<string, string>();
    const idToTitelFunktionMap = new Map<string, string>();
    const idToPrefixMap = new Map<string, string>();
    titleData
      .filter(d => d.year === selectedYear)
      .forEach(d => {
        idMap.set(d.id, d.zweckbestimmung);
        if (d.titel_funktion) idToTitelFunktionMap.set(d.id, d.titel_funktion);
        if (d.prefix) idToPrefixMap.set(d.id, d.prefix);
      });

    // Fallback: for ids not present in selected year, use any available name
    titleData.forEach(d => {
      if (!idMap.has(d.id)) {
        idMap.set(d.id, d.zweckbestimmung);
        if (d.titel_funktion) idToTitelFunktionMap.set(d.id, d.titel_funktion);
        if (d.prefix) idToPrefixMap.set(d.id, d.prefix);
      }
    });

    // Use global Ist year detection (not affected by sidebar filters)
    const useIst = globalYearsWithIst.has(selectedYear);

    // Aggregate by id for the selected year
    const idTotals = new Map<string, { value: number; isIst: boolean }>();
    titleData
      .filter(d => d.year === selectedYear)
      .forEach(d => {
        const existing = idTotals.get(d.id);
        const rawValue = useIst ? (d.total_ist ?? 0) : d.total_soll;
        const convertedValue = convertValue(rawValue, selectedYear);
        if (existing) {
          existing.value += convertedValue;
        } else {
          idTotals.set(d.id, { value: convertedValue, isIst: useIst && d.total_ist !== null });
        }
      });

    // Convert to array and sort
    const sortedEntries = [...idTotals.entries()].sort((a, b) => b[1].value - a[1].value);

    let colorIndex = 0;
    const chartData = sortedEntries.map(([id, data]) => {
      // Use passed color map from parent for consistency, with local fallback
      const color = propsTitleColorMap?.get(id) ?? (id === SONSTIGES_ID ? SONSTIGES_COLOR : getSubTypeColor(colorIndex++));
      return {
        type: id,  // Use id as type, display name looked up from idMap
        // Only the synthetic Sonstiges bucket has a translatable name.
        displayName: id === SONSTIGES_ID ? categoryLabel(idMap.get(id) || id) : idMap.get(id) || id,
        value: data.value,
        isIst: data.isIst,
        color,
      };
    });

    return { data: chartData, idMap, idToTitelFunktionMap, idToPrefixMap };
  }, [titleData, selectedYear, propsTitleColorMap, convertValue, globalYearsWithIst]);

  // Compute effective single title for single-title mode
  const effectiveSingleTitle = useMemo(() => {
    if (selectedTitle) return selectedTitle;

    if (drillDownSubType !== null && titleChartData.data.length === 1) {
      const entry = titleChartData.data[0];
      return {
        id: entry.type,
        zweckbestimmung: entry.displayName || entry.type,
        titelFunktion: titleChartData.idToTitelFunktionMap.get(entry.type),
        prefix: titleChartData.idToPrefixMap.get(entry.type),
      };
    }

    return null;
  }, [selectedTitle, drillDownSubType, titleChartData.data]);

  const handleBarClick = (type: string) => {
    if (!interactive) return;
    if (drillDownSubType !== null) {
      // Skip click handling for SONSTIGES - it's a synthetic aggregate
      if (type === SONSTIGES_ID) return;
      // Level 3: clicking on a title filters the table
      // `type` is now the id, look up the display name
      const displayName = titleChartData.idMap.get(type) || type;
      const titelFunktion = titleChartData.idToTitelFunktionMap.get(type);
      const prefix = titleChartData.idToPrefixMap.get(type);
      onFilterByTitle?.(type, displayName, titelFunktion, prefix);
    } else if (drillDownType && subChartData.length > 0) {
      // Level 2: clicking on a sub-type drills down to titles
      if (!type) return;
      onDrillDownSub(type);
    } else {
      // Level 1: clicking on a main type drills down to sub-types
      onDrillDown(type);
    }
  };

  const handleBack = () => {
    onDrillUp();
  };

  const isIstYear = yearsWithIst.has(selectedYear);

  // Select display data based on drill-down level
  // Note: drillDownSubType can be empty string '' for Einnahmen (no sub-types), so check !== null
  const displayData = drillDownSubType !== null
    ? (effectiveSingleTitle
        ? titleChartData.data.filter(d => d.type === effectiveSingleTitle.id)
        : titleChartData.data)
    : drillDownType
    ? subChartData
    : chartData;

  // Calculate max value for consistent axis formatting
  const maxBarValue = useMemo(() => {
    if (!displayData || displayData.length === 0) return 0;
    return Math.max(...displayData.map(d => d.value));
  }, [displayData]);

  // Tick formatter without units — used on axis ticks only
  const axisTickFormatter = useMemo(() => {
    return displayUnit === 'gdp_percent' || displayUnit === 'budget_percent'
      ? formatGdpPercentageTick
      : createAxisFormatter(maxBarValue);
  }, [displayUnit, maxBarValue]);

  const yAxisLabel = useMemo(() => {
    if (displayUnit === 'gdp_percent') return m.units.gdpPercent;
    if (displayUnit === 'budget_percent') return m.units.budgetPercent;
    return getAxisUnit(maxBarValue).suffix;
  }, [displayUnit, maxBarValue, m]);

  // Build breadcrumb items
  const breadcrumbItems: { label: string; onClick?: () => void }[] = [];

  // Always show root - clickable when drilled down
  const rootLabel = budgetType === 'E'
    ? m.chart.barRootRevenue(selectedYear)
    : m.chart.barRootExpenditure(selectedYear);
  breadcrumbItems.push({
    label: rootLabel,
    onClick: drillDownType ? onNavigateToRoot : undefined
  });

  if (drillDownType) {
    // Level 2+: show drill-down type (clickable if deeper)
    breadcrumbItems.push({
      label: categoryLabel(drillDownType),
      onClick: drillDownSubType !== null ? onNavigateToLevel2 : undefined
    });

    // Level 3+: show sub-type (skip empty string placeholders for Einnahmen)
    if (drillDownSubType && drillDownSubType !== '') {
      breadcrumbItems.push({
        label: categoryLabel(drillDownSubType),
        onClick: effectiveSingleTitle ? onDeselectTitle : undefined
      });
    }

    // Level 4: show selected title with prefix + titel_funktion
    if (effectiveSingleTitle) {
      const code = [effectiveSingleTitle.prefix, effectiveSingleTitle.titelFunktion].filter(Boolean).join(' ');
      const titleLabel = code ? `(${code}) ${effectiveSingleTitle.zweckbestimmung}` : effectiveSingleTitle.zweckbestimmung;
      breadcrumbItems.push({ label: titleLabel });
    }
  }

  // A subtitle is a description plus a click hint; a locked chart (an embed)
  // drops the hint: the reader can't click, so the instruction would be a lie.
  // Done once here so new instruction text can't leak into an embed.
  const withHint = (text: string, hint: string) =>
    interactive ? `${text}${m.chart.hintSeparator}${hint}` : text;

  const getChartSubtitle = () => {
    if (effectiveSingleTitle) {
      return m.chart.barSingleTitle;
    }
    if (drillDownSubType) {
      return withHint(m.chart.topTitles, m.chart.hintFilterTable);
    }
    if (drillDownType) {
      return withHint(m.chart.subTypes, m.chart.hintDrill);
    }
    return withHint(isIstYear ? m.chart.barRootIst : m.chart.barRootSoll, m.chart.hintDrill);
  };

  // Build chart title/breadcrumb for export
  const chartBreadcrumb = breadcrumbItems.map(item => item.label);

  // Compute filter info text
  const filterInfoText = useMemo(() => {
    return buildFilterInfoText(activeFilters, filterOptions);
  }, [activeFilters, filterOptions]);

  // Flush-left y-axis (publishing chart engine): axis width = widest tick
  // label + 14px, chart margin.left = 0 → widest label flush with the title.
  // The returned value is also the plot's left edge — the unit caption and
  // the bottom legend anchor there, exactly like the engine.
  const yAxisWidth = useFlushLeftYAxisWidth(chartContainerRef, [displayData, displayUnit, isCollapsed]);

  // Build legend items for export
  const getLegendItems = (): LegendItem[] => {
    return displayData.map(entry => {
      const displayLabel = entry.displayName || entry.type;
      const dashed = entry.type === HAUSHALTSTECHNIK_TYPE;
      return { name: displayLabel, color: entry.color, dashed };
    });
  };

  const getExportOptions = (): ChartExportOptions => ({
    // The export renders only the breadcrumb path (title = deepest level,
    // subtitle = parent path) — the on-screen subtitle is not exported.
    breadcrumb: chartBreadcrumb,
    legend: getLegendItems(),
    filterInfo: filterInfoText || undefined,
    axisUnit: yAxisLabel,
    axisUnitX: yAxisWidth,
  });

  const handleExportPng = () => {
    const svgElement = chartContainerRef.current?.querySelector('svg.recharts-surface');
    if (svgElement) {
      exportChartAsPng(svgElement as SVGElement, getExportOptions());
    }
  };

  const handleExportSvg = () => {
    const svgElement = chartContainerRef.current?.querySelector('svg.recharts-surface');
    if (svgElement) {
      exportChartAsSvg(svgElement as SVGElement, getExportOptions());
    }
  };

  // Show loading state
  if (isLoading || (drillDownType && isLoadingSubData) || (drillDownSubType !== null && isLoadingTitleData)) {
    return (
      <Section>
        <LoadingOverlay>
          <Spinner />
        </LoadingOverlay>
      </Section>
    );
  }

  // Collapsed state: show mini vertical bars preview
  if (isCollapsed) {
    return (
      <CollapsedPreview onClick={() => setIsCollapsed(false)}>
        <PreviewHeader>
          <PreviewLeftSection>
            <PreviewYear>{selectedYear}</PreviewYear>
            <MiniChartContainer>
              {collapsedMetrics?.segments.map((seg) => (
                <MiniBar
                  key={seg.name}
                  height={`${(seg.value / (collapsedMetrics?.maxValue || 1)) * 100}%`}
                  color={seg.color}
                  title={`${categoryLabel(seg.name)}: ${tooltipValueFormatter(seg.value)}`}
                />
              ))}
            </MiniChartContainer>
          </PreviewLeftSection>
          <ExpandLabel>{m.chart.expand}</ExpandLabel>
          <ChevronIcon isExpanded={false} />
        </PreviewHeader>
      </CollapsedPreview>
    );
  }

  return (
    <Section>
      <FlexRow justify="space-between" align="flex-start" style={{ marginBottom: theme.spacing.md }}>
        <FlexRow gap={theme.spacing.sm}>
          {interactive && (drillDownType || drillDownSubType) && (
            <BackButton variant="ghost" size="s" onClick={handleBack} aria-label={m.common.back}>
              <BackIcon />
            </BackButton>
          )}
          <div>
            <BreadcrumbContainer>
              {breadcrumbItems.map((item, index) => (
                <span key={index} style={{ display: 'flex', alignItems: 'center' }}>
                  {index > 0 && (
                    <BreadcrumbSeparator>
                      <span className="icon" data-icon="chevron_right" />
                    </BreadcrumbSeparator>
                  )}
                  <BreadcrumbItem
                    isLast={index === breadcrumbItems.length - 1}
                    isClickable={!!item.onClick}
                    onClick={item.onClick}
                  >
                    {item.label}
                  </BreadcrumbItem>
                </span>
              ))}
            </BreadcrumbContainer>
            {filterInfoText && <FilterInfoLine>{filterInfoText}</FilterInfoLine>}
            {!filterInfoText && <CardSubtitle>{getChartSubtitle()}</CardSubtitle>}
          </div>
        </FlexRow>

        <FlexRow gap={theme.spacing.md}>
          {activeType && onClearFilter && !drillDownType && drillDownSubType === null && (
            <ActiveFilterChip>
              <ChipText>{categoryLabel(activeType)}</ChipText>
              <ClearChipButton onClick={onClearFilter}>
                <ClearIcon />
              </ClearChipButton>
            </ActiveFilterChip>
          )}
          {!hideYearSelect && (
            <YearSelect
              value={selectedYear}
              onChange={e => onYearChange(Number(e.target.value))}
            >
              {years.map(year => (
                <option key={year} value={year}>
                  {year}{!yearsWithIst.has(year) ? ' *' : ''}
                </option>
              ))}
            </YearSelect>
          )}
          {onEmbed && <EmbedButton onClick={onEmbed} />}
          {showExport && (
            <ExportDropdown onExportPng={handleExportPng} onExportSvg={handleExportSvg} />
          )}
          {!startExpanded && (
            <ExpandButton onClick={() => setIsCollapsed(true)}>
              <ChevronIcon isExpanded={true} />
            </ExpandButton>
          )}
        </FlexRow>
      </FlexRow>

      {displayData.length === 0 ? (
            <EmptyState>{m.chart.noDataForYear(selectedYear)}</EmptyState>
          ) : (
            <ChartContainer ref={chartContainerRef}>
              {/* Caption starts exactly at the axis line (= plot left edge),
                  like the engine's unit caption (drawYAxis: x=0, anchor start). */}
              <AxisUnitLabel style={{ paddingLeft: yAxisWidth }}>{yAxisLabel}</AxisUnitLabel>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={displayData}
                  /* margin.left = 0: the dynamic YAxis width reserves exactly
                     the widest tick label + 14px, putting that label's left
                     edge flush with the title (engine flush-left rule). */
                  margin={{ top: 10, right: 30, left: 0, bottom: 10 }}
                >
                  {/* dz-publishing-process chart engine: horizontal-only
                      grid, black 1px dashes (4px dash / 4px gap) at reduced
                      opacity so the dashes read as a faint locating aid;
                      solid black axis lines with 6px tick marks, 8px
                      tick-label padding; 13px black sans tick labels.
                      Unlike stacked areas, the engine's bar renderer keeps
                      the grid BENEATH the bars — declared first here. */}
                  <CartesianGrid strokeDasharray="4 4" stroke="#000000" strokeOpacity={0.5} vertical={false} />
                  <XAxis
                    type="category"
                    dataKey="type"
                    tick={false}
                    tickLine={false}
                    axisLine={{ stroke: '#000000' }}
                  />
                  <YAxis
                    type="number"
                    tickFormatter={axisTickFormatter}
                    tick={{ fontSize: 13, fill: '#000000', fontFamily: 'var(--sans-stack)' }}
                    tickLine={{ stroke: '#000000' }}
                    tickSize={6}
                    tickMargin={8}
                    axisLine={{ stroke: '#000000' }}
                    width={yAxisWidth}
                    allowDecimals={displayUnit !== 'gdp_percent' && displayUnit !== 'budget_percent'}
                    domain={displayUnit === 'budget_percent' ? [0, 100] : undefined}
                  />
                  <Tooltip
                    content={<CustomTooltip formatter={tooltipValueFormatter} />}
                  />
                  {/* Publishing chart engine: 8px rounded bar tips, fully
                      opaque fills. Haushaltstechnik is the Figma exception:
                      dashed black 1px outline, no fill. */}
                  <Bar
                    dataKey="value"
                    radius={[8, 8, 0, 0]}
                    onClick={(data) => {
                      if (data && data.type) {
                        handleBarClick(data.type);
                      }
                    }}
                  >
                    {displayData.map((entry, index) => {
                      const isSonstiges = drillDownSubType !== null && entry.type === SONSTIGES_ID;
                      const isHaushaltstechnik = entry.type === HAUSHALTSTECHNIK_TYPE;
                      return (
                        <Cell
                          key={`cell-${index}`}
                          fill={entry.color}
                          fillOpacity={
                            isHaushaltstechnik
                              ? 0.05
                              : (drillDownType ? 1 : (
                                  activeType === null || activeType === undefined
                                    ? 1
                                    : activeType === entry.type
                                    ? 1
                                    : 0.3
                                ))
                          }
                          stroke={isHaushaltstechnik ? '#000000' : undefined}
                          strokeWidth={isHaushaltstechnik ? 1 : undefined}
                          strokeDasharray={isHaushaltstechnik ? "2 2" : undefined}
                          cursor={isSonstiges ? 'default' : 'pointer'}
                        />
                      );
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              {/* Legend rendered OUTSIDE recharts so the plot keeps its full
                  fixed height — wrapping legend rows grow the section downward
                  instead of shrinking the chart (engine behavior: plot size is
                  fixed, legend space is added below). Flush-left with the
                  title edge. */}
              <LegendContainer>
                {displayData.map(entry => {
                  // Use displayName if available (title level), otherwise use type
                  const displayLabel = entry.displayName || entry.type;
                  const isSonstiges = drillDownSubType !== null && entry.type === SONSTIGES_ID;
                  return (
                    <LegendItem
                      key={entry.type}
                      dimmed={!drillDownType && activeType !== null && activeType !== entry.type}
                      isClickable={interactive && !isSonstiges}
                      onClick={() => handleBarClick(entry.type)}
                    >
                      <LegendDot
                        color={entry.color}
                        dashed={entry.type === HAUSHALTSTECHNIK_TYPE}
                      />
                      <LegendLabel>{displayLabel}</LegendLabel>
                    </LegendItem>
                  );
                })}
              </LegendContainer>
            </ChartContainer>
          )}

      {!isIstYear && (
        <Caption>{m.chart.sollYearsCaption}</Caption>
      )}
    </Section>
  );
}
